import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import {
  UncontrolledSegmentedButtonChildren,
  UncontrolledSegmentedButtonParent,
} from 'components/Form';
import cx from 'classnames';
import { Button, Field } from '@ontruck/orc';
import React, { forwardRef, useCallback, useEffect } from 'react';
import { useVenueOpeningHoursEdit, VenueOpeningHoursEditProvider } from './context';
import _ from 'lodash';

const t = {
  weekDayMapTiny: {
    monday: 'M',
    tuesday: 'T',
    wednesday: 'W',
    thursday: 'Th',
    friday: 'F',
    saturday: 'S',
    sunday: 'Su',
  },
  addScheduleSegment: 'Add schedule segment',
  timeFrom: 'From',
  timeTo: 'To',
};

const DEFAULT_SCHEDULE_VALUE = {
  schedule_days: [
    { selected: false },
    { selected: false },
    { selected: false },
    { selected: false },
    { selected: false },
    { selected: false },
    { selected: false },
  ],
  schedule_ranges: [
    {
      from: '00:00',
      to: '23:59',
    },
  ],
};

const DEFAULT_RANGE_VALUE = {
  from: '00:00',
  to: '23:59',
};

const DaySelector = ({ fieldName, dayIndex, day, isDayUsed }) => {
  const { field } = useController({
    name: `${fieldName}.selected`,
    defaultValue: day.selected,
  });

  const dayName = Object.values(t.weekDayMapTiny)[dayIndex];

  return (
    <UncontrolledSegmentedButtonChildren
      type="checkbox"
      disabled={isDayUsed && !field.value}
      checked={field.value}
      value={field.value}
      {...field}
    >
      {dayName}
    </UncontrolledSegmentedButtonChildren>
  );
};

const SegmentedDaySelector = ({ fieldName }) => {
  const { control } = useFormContext();

  const { fields } = useFieldArray({
    control,
    name: fieldName,
    shouldUnregister: false,
  });

  const { getIsDayUsed } = useVenueOpeningHoursEdit();

  return (
    <div className="schedule-days-container">
      <UncontrolledSegmentedButtonParent>
        {fields.map((day, i) => (
          <DaySelector
            key={day.id}
            fieldName={`${fieldName}.${i}`}
            day={day}
            isDayUsed={getIsDayUsed(i)}
            dayIndex={i}
          />
        ))}
      </UncontrolledSegmentedButtonParent>
    </div>
  );
};

const RangeSelector = forwardRef(({ fieldName, range, append, isLast, error }) => {
  const { getValues } = useFormContext();
  const fromFieldName = `${fieldName}.from`;
  const toFieldName = `${fieldName}.to`;

  const validateRange = useCallback(() => {
    const fromValue = getValues(fromFieldName);
    const toValue = getValues(toFieldName);
    return fromValue < toValue;
  }, [fromFieldName, toFieldName, getValues]);

  const { field: fromField } = useController({
    name: fromFieldName,
    defaultValue: range.from,
    shouldUnregister: false,
    rules: {
      required: true,
      minLength: 5,
      validate: validateRange,
    },
  });
  const { field: toField } = useController({
    name: toFieldName,
    defaultValue: range.to,
    shouldUnregister: false,
    rules: {
      required: true,
      minLength: 5,
      validate: validateRange,
    },
  });

  const handleAdd = useCallback(() => {
    append(DEFAULT_RANGE_VALUE);
  }, [append]);

  return (
    <div className="field-container time-field-container">
      <div>
        <span
          className={cx({
            hidden: !isLast,
          })}
        >
          {t.timeFrom}
        </span>
        <Field
          className="input"
          maskedProps={{ mask: '99:99', maskChar: null }}
          error={!!error?.from}
          {...fromField}
        />
      </div>
      <div>
        <span
          className={cx({
            hidden: !isLast,
          })}
        >
          {t.timeTo}
        </span>
        <Field
          className="input"
          maskedProps={{ mask: '99:99', maskChar: null }}
          error={!!error?.to}
          {...toField}
        />
      </div>
      {isLast && <Button ghost onClick={handleAdd} icon="plus" className="rounded-btn" />}
    </div>
  );
});

const VenueOpeningHoursScheduleSegment = forwardRef(({ fieldName, error }) => {
  const daysFieldName = `${fieldName}.schedule_days`;
  const rangesFieldName = `${fieldName}.schedule_ranges`;

  const { control } = useFormContext();
  const { fields, append } = useFieldArray({
    control,
    name: rangesFieldName,
    shouldUnregister: false,
  });

  return (
    <div className="schedule-segment-container">
      <SegmentedDaySelector fieldName={daysFieldName} />

      {fields.map((range, i) => (
        <RangeSelector
          key={range.id}
          fieldName={`${rangesFieldName}.${i}`}
          append={append}
          isLast={fields.length - 1 === i}
          range={range}
          error={error?.schedule_ranges?.[i]}
        />
      ))}
    </div>
  );
});

const VenueOpeningHoursEditMode = ({ schedules, fieldName, error }) => {
  const { setValue } = useFormContext();
  const { fields, append } = useFieldArray({
    name: fieldName,
    shouldUnregister: false,
  });

  const handleAdd = useCallback(() => {
    append(_.cloneDeep(DEFAULT_SCHEDULE_VALUE));
  }, [append]);

  useEffect(() => {
    if (!schedules) {
      // When creating
      setValue(fieldName, [_.cloneDeep(DEFAULT_SCHEDULE_VALUE)]);
    } else {
      // When editing
      setValue(fieldName, schedules);
    }
    //eslint-disable-next-line
  }, [fieldName]);

  return (
    <VenueOpeningHoursEditProvider fieldName={fieldName}>
      <div className="field-container">
        {fields.map((schedule, i) => (
          <VenueOpeningHoursScheduleSegment
            key={schedule.id}
            fieldName={`${fieldName}.${i}`}
            error={error?.[i]}
          />
        ))}
        <Button
          text={t.addScheduleSegment}
          nude
          onClick={handleAdd}
          icon="plus"
          iconPosition="left"
        />
      </div>
    </VenueOpeningHoursEditProvider>
  );
};

export default VenueOpeningHoursEditMode;
